import React, { useState } from 'react';
import clsx from 'clsx';
import { LoaderIcon } from '@dashboard/components/ui/loader-icon';

interface Props {
  src: string;
  previewKey: string | number;
  title?: string;
  /** Fired when the iframe finishes loading the underlying URL. Callers
   *  use this to drive their own "Refreshing…" state. */
  onLoad?: () => void;
  /** Mobile and tablet show the email at that real width; desktop scales it to fit. */
  deviceSize?: 'mobile' | 'tablet' | 'desktop';
}

const DEVICE_WIDTHS = { mobile: 390, tablet: 768 };

const MailIframePreview: React.FC<Props> = ({ src, previewKey, title, onLoad, deviceSize }) => {
  const [loaded, setLoaded] = useState(false);
  const deviceWidth = deviceSize && deviceSize !== 'desktop' ? DEVICE_WIDTHS[deviceSize] : null;

  return (
    <div
      className={clsx(
        'relative mx-auto h-full w-full',
        deviceWidth && 'overflow-hidden rounded-lg border border-gray-100 dark:border-border',
      )}
      style={deviceWidth ? { maxWidth: deviceWidth } : undefined}
    >
      <iframe
        key={previewKey}
        title={title}
        src={src}
        onLoad={() => {
          setLoaded(true);
          onLoad?.();
        }}
        className={clsx(
          'origin-top-left bg-white dark:bg-background transition-opacity duration-200',
          loaded ? 'opacity-100' : 'opacity-0',
          deviceWidth
            ? 'h-full w-full'
            : [
                'scale-50 w-[200%] h-[calc(100%/0.5)]',
                'xl:scale-75 xl:w-[133.33%] xl:h-[calc(100%/0.75)]',
                '3xl:scale-100 3xl:w-full 3xl:h-full',
              ],
        )}
      />

      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-background">
          <LoaderIcon className="size-5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default MailIframePreview;
